import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    text = "text",
    registerToAccessBenefit = "registerToAccessBenefit",
    applyAccountTextfiled = "applyAccountTextfiled",
    fullWidthButton = "fullWidthButton",
}

const mapStateToProps = (state: ApplicationState) => {
    // const { search } = getLocation(state);
    let { search } = getLocation(state);
    const { allowCreateAccount, allowGuestCheckout } = getSettingsCollection(state).accountSettings;
    let returnUrl = getReturnUrl(state);
    if (!returnUrl && state.components.signInOverlay.isOpen) {
        returnUrl = getLocation(state).pathname;
        if (!search) {
            search = `?returnUrl=${returnUrl}`;
        }
    }
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    return {
        allowCreateAccount,
        allowGuestCheckout:
            allowGuestCheckout &&
            !getCurrentUserIsGuest(state) &&
            (referredFromShipping || referredFromOnePageCheckout),
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.text]: string;
        [fields.registerToAccessBenefit]: string;
        [fields.applyAccountTextfiled]: string;
        [fields.fullWidthButton]: boolean;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SignInCreateNewAccountStyles {
    wrapper?: InjectableCss;
    signInCreateNewAccountGridContainer?: GridContainerProps;
    signInCreateNewAccountTitleGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItem?: GridItemProps;
    signInCreateNewAccountButtonGridItem?: GridItemProps;
    signInCreateNewAccountTitle?: TypographyProps;
    signInCreateNewAccountText?: TypographyProps;
    checkoutAsGuestButton?: ButtonPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
    registerToAccessWrapp?: InjectableCss;
    applyAccountWrapp?: InjectableCss;
}

export const signInCreateNewAccountStyles: SignInCreateNewAccountStyles = {
    wrapper: {
        css: css`
            margin-bottom: 0;
            height: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 30px !important;
                    `,
                    css`
                        margin-top: 30px !important;
                    `,
                    css`
                        margin-top: 30px !important;
                    `,
                    null,
                    null,
                ])}
        `,
    },

    signInCreateNewAccountTitle: {
        variant: "h4",
        css: css`
            background: ${getColor("common.border")};
            color: ${getColor("text.accent")};
            font-family: "MontserratMedium";
            padding: 19px 32px;
            margin-bottom: 0;
            border-top-left-radius: 10px;
            border-top-right-radius: 10px;
            padding-bottom: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 15px 20px;
                        font-size: 16px;
                    `,
                    css`
                        padding: 15px 20px;
                        font-size: 16px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    signInCreateNewAccountGridContainer: {
        css: css`
            background-color: ${getColor("secondary")};
            padding: 19px 32px;
            border-bottom-left-radius: 10px;
            border-bottom-right-radius: 10px;
            height: 100%;
            max-width: 100% !important;
            font-family: "MontserratRegular";
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 20px;
                    `,
                    css`
                        padding: 20px;
                    `,
                    null,
                    null,
                    null,
                ])}
            >${GridOffset} {
                height: 330px;
            }
        `,
    },

    registerToAccessWrapp: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            width: 100%;

            > ${TypographyStyle} {
                width: 100%;
                > ul[class^="UnorderedListStyle"] {
                    margin-top: 30px !important;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    margin-block: 0;
                    padding-left: 40px;
                }
            }
            > ${TypographyStyle} > ul[class^="UnorderedListStyle"] > li {
                margin-bottom: 5px;
            }
            > ${TypographyStyle} > ul[class^="UnorderedListStyle"] ~ ${TypographyStyle} {
                display: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        > ${TypographyStyle} > ul[class^="UnorderedListStyle"] {
                            grid-template-columns: 1fr;
                            padding-left: 20px;
                        }
                    `,
                    css`
                        > ${TypographyStyle} > ul[class^="UnorderedListStyle"] {
                            grid-template-columns: 1fr;
                            padding-left: 20px;
                        }
                    `,
                    css`
                        > ${TypographyStyle} > ul[class^="UnorderedListStyle"] {
                            grid-template-columns: 1fr;
                            padding-left: 20px;
                        }
                    `,
                    css`
                        > ${TypographyStyle} > ul[class^="UnorderedListStyle"] {
                            grid-template-columns: 1fr;
                            padding-left: 20px;
                        }
                    `,
                    null,
                ])}
        `,
    },

    applyAccountWrapp: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            > ${TypographyStyle} > ${TypographyStyle} {
                margin-bottom: 10px;
            }
        `,
    },

    signInCreateNewAccountTitleGridItem: {
        width: 12,
    },
    signInCreateNewAccountTextGridItem: {
        width: 12,
    },
    signInCreateNewAccountText: {
        css: css`
            color: ${getColor("text.accent")};
        `,
    },

    signInCreateNewAccountButtonGridItem: {
        width: 12,
        css: css`
            padding-bottom: 38px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        null,
                        null,
                        null,
                        css`
                            flex-direction: row;
                            justify-content: space-between;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    checkoutAsGuestButton: {
        variant: "tertiary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            margin-bottom: 1rem;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    createNewAccountButton: { variant: "secondary" },
};

/**
 * @deprecated Use signInCreateNewAccountStyles instead.
 */
export const signInCreateNewAccount = signInCreateNewAccountStyles;
const styles = signInCreateNewAccountStyles;

const SignInCreateNewAccount: FC<Props> = ({
    history,
    fields,
    allowCreateAccount,
    allowGuestCheckout,
    returnUrl,
    createAccountUrl,
    signInAsGuest,
}) => {
    if (!allowCreateAccount) {
        return null;
    }

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        signInAsGuest({ returnUrl });
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }
        history.push(createAccountUrl);
    };

    const createButtonCss = fields.fullWidthButton
        ? css`
              width: 100%;
          `
        : undefined;

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.signInCreateNewAccountTitle}>{translate("Create New Account")}</Typography>
            <GridContainer {...styles.signInCreateNewAccountGridContainer}>
                <GridItem {...styles.registerToAccessWrapp}>
                    <Typography className="description-text">
                        {parse(fields.registerToAccessBenefit, parserOptions)}
                    </Typography>
                </GridItem>

                <GridItem {...styles.signInCreateNewAccountTextGridItem}>
                    <Typography {...styles.signInCreateNewAccountText}>{fields.text}</Typography>
                </GridItem>

                <GridItem {...styles.signInCreateNewAccountButtonGridItem}>
                    {/* GuestCheckout flow is not required for this project */}

                    {/* {allowGuestCheckout && (
                        <Button
                            {...styles.checkoutAsGuestButton}
                            onClick={handleCheckoutAsGuest}
                            data-test-selector="signInCreateNewAccount_checkoutAsGuest"
                        >
                            {translate("Checkout as Guest")}
                        </Button>
                    )} */}
                    {createAccountUrl && (
                        <Button
                            {...styles.createNewAccountButton}
                            css={createButtonCss}
                            onClick={handleCreateNewAccount}
                            data-test-selector="signInCreateNewAccount_createNewAccount"
                        >
                            {translate("Click to Register")}
                        </Button>
                    )}
                </GridItem>

                <GridItem {...styles.applyAccountWrapp}>
                    <Typography className="description-text">
                        {parse(fields.applyAccountTextfiled, parserOptions)}
                    </Typography>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};
const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(SignInCreateNewAccount)),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.text,
                displayName: "Main Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "Create an account to checkout faster, view order history, save product lists, and more!",
                fieldType: "Translatable",
                tab: basicTab,
                sortOrder: 0,
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.registerToAccessBenefit,
                displayName: "registerTo Access Benefit",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                extendedConfig: { height: 200 },
                tab: basicTab,
                sortOrder: 2,
            },
            {
                name: fields.applyAccountTextfiled,
                displayName: "apply Account Textfiled",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                extendedConfig: { height: 200 },
                tab: basicTab,
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
